import { EVENT_NAME_MAPPING, KioskEvent } from "./types";
import { Variables } from ".";
import { filterEmpty } from "./utils";
import { isEmpty, map, omit, pick, startCase } from "lodash";
import { captureErrorAsync } from "../utils/capture-error-async";
import { logger } from "./logger";
import { pushQueue } from "./event-queue";
import {
  analyticsTrack,
  floorlyticsBlob,
  defaultTraits,
} from "../utils/analytics";

export let PREVIOUS_PAGE_NAME: string | null = null;

function filterInfo(event: KioskEvent) {
  const { eventData: data } = event;
  const val = (type: string) => (value: string) => ({ type, value });

  const brandFilters = map(data.filteredBrands, val("brand"));
  const categoryFilters = map(data.filteredTypes, val("category"));
  const colorFilters = map(data.filteredColors, val("color"));
  const filters = [...brandFilters, ...categoryFilters, ...colorFilters];

  if (filters.length !== 0) {
    return { filters };
  } else {
    return {};
  }
}

function mapProperties(
  event: KioskEvent,
  _variables: Variables,
  featureContext: FeatureContext
) {
  const { eventFeature } = event;
  const {
    occurredAt,
    dealerUUID,
    dealerName,
    id,
    extId,
    brandName,
    ...unfilteredEventData
  } = event.eventData;
  const extraProps: { [key: string]: string } = {};
  const eventData = omit(unfilteredEventData, [
    "userSession",
    "currentPage",
    "filteredTypes",
    "filteredColors",
    "filteredBrands",
  ]);

  const props = filterEmpty({
    ...eventData,
    ...extraProps,
    ...featureContext,
    ...filterInfo(event),
    id: id,
    // eslint-disable-next-line @typescript-eslint/camelcase
    product_id: extId,
    brand: brandName,
    application: "viz",
    category: eventFeature,
    universalRetailerId: featureContext.universalRetailerId || dealerUUID,
    retailerName: dealerName,
    label: event.eventResult,
    ...defaultTraits(),
  });

  const ctx = filterEmpty({
    app: {
      name: "viz",
    },
    device: filterEmpty({
      id: unfilteredEventData.deviceId,
    }),
    groupId: props["universalRetailerId"],
    timestamp: occurredAt,
  });

  return [props, ctx];
}

function removeFloorlyticsBlob() {
  const tags = document.querySelectorAll<HTMLMetaElement>(
    'meta[name="floorlytics-blob"]'
  );

  tags.forEach((tag) => {
    tag.parentNode?.removeChild(tag);
  });
}

async function setFloorlyticsBlog() {
  const tag = document.createElement("meta");
  tag.name = "floorlytics-blob";
  tag.content = await floorlyticsBlob();
  document.head.appendChild(tag);
}

function reset() {
  PREVIOUS_PAGE_NAME = null;
  analytics.reset();
  removeFloorlyticsBlob();
}

function maybeTrackPageview(
  event: KioskEvent,
  variables: Variables,
  featureContext: FeatureContext
) {
  const { eventFeature } = event;

  // Do not double count pageviews for web
  if (variables.kiosk !== true) {
    return;
  }

  const pageName = eventFeature;

  if (pageName !== PREVIOUS_PAGE_NAME) {
    PREVIOUS_PAGE_NAME = pageName;
    const [props, context] = mapProperties(event, variables, featureContext);
    analytics.page(`Viewed ${pageName} Page`, props, context);
  }
}

function maybeIdentify(...args: [object, object]) {
  const [props, context] = args;
  const traits = filterEmpty(pick(props, ["email", "phoneNumber"]));

  if (!isEmpty(traits)) {
    analytics.identify(traits, context);
  }
}

function determineEventName(event: KioskEvent) {
  const { eventName, eventAction } = event;

  return (
    EVENT_NAME_MAPPING[eventAction] ||
    EVENT_NAME_MAPPING[eventName] ||
    eventAction ||
    startCase(eventName)
  );
}

function isNewSession(event: KioskEvent) {
  const userSession = event.eventData.userSession;

  if (userSession == null) {
    return false;
  }

  return userSession !== analytics.user().anonymousId();
}

function startNewSession(event: KioskEvent) {
  logger.info("Detected new session ID, initializing new session");
  reset();
  const sessionId = event?.eventData?.userSession;

  if (sessionId) {
    analytics.setAnonymousId(sessionId);
  }

  removeFloorlyticsBlob();
  setFloorlyticsBlog();
}

function handleEvent(
  event: KioskEvent,
  variables: Variables,
  featureContext: FeatureContext
) {
  try {
    const { eventData } = event;

    if (isNewSession(event) && eventData.userSession) {
      startNewSession(event);
    }

    const eventName = determineEventName(event);
    const [props, context] = mapProperties(event, variables, featureContext);

    maybeTrackPageview(event, variables, featureContext);
    maybeIdentify(props, context);
    analyticsTrack(eventName, props, context);
  } catch (e) {
    logger.error(`Error when dispatching '${event.eventName}'`, e);
    captureErrorAsync(e);
  }
}

export function dispatchEvent(
  event: KioskEvent,
  variables: Variables,
  featureContext: FeatureContext
) {
  pushQueue(event, variables, featureContext, handleEvent);
}
